import React, { MouseEvent, Component } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { Button, Popover, Typography } from "@material-ui/core";
import ProgressSpinner from "components/ProgressSpinner";
import { loadExpandedBookings } from "services/bookings";
import { PromiseContainer } from "util/promises";
import NextRouteURLs from "NextRouteURLs";
import { handleError } from "util/errors";
import "./index.scss";
import { ExpandedBooking } from "services/entities/booking";
import { DateTime } from "util/time";
import NewResourceButton from "components/NewResourceButton";
import {
  AttachmentIcon,
  MoreIcon,
  SuccessIcon,
  WarningIcon,
} from "components/Icon";
import { ToggledContent } from "components/ToggledContent";
import ExpandToggleButton from "pages/Dashboard/components/ExpandToggleButton";
import BookingDesignModal from "pages/Dashboard/components/BookingDesignModal";
import { formatBookingDate, formatBookingTime } from "util/booking";

interface BookingsSectionState {
  futureBookings: ExpandedBooking[] | null;
  bookingMenuBooking: ExpandedBooking | null;
  bookingsExpanded: boolean;
  bookingMenuAnchorEl: HTMLElement | null;
  designModalOpen: boolean;
  designModalBooking: ExpandedBooking | null;
}

class BookingsSection extends Component<
  RouteComponentProps,
  BookingsSectionState
> {
  private promiseContainer: PromiseContainer | null = null;

  state: BookingsSectionState = {
    futureBookings: null,
    bookingsExpanded: true,
    bookingMenuBooking: null,
    bookingMenuAnchorEl: null,
    designModalOpen: false,
    designModalBooking: null,
  };

  componentDidMount(): void {
    this.promiseContainer = new PromiseContainer();
    this.loadBookings(this.promiseContainer);
  }

  componentWillUnmount(): void {
    if (this.promiseContainer) {
      this.promiseContainer.dispose();
      this.promiseContainer = null;
    }
  }

  render() {
    const futureBookings = this.state.futureBookings;
    const bookingsCount = futureBookings?.length || 0;
    const bookingsLoaded = !!futureBookings;
    const expanded = this.state.bookingsExpanded;
    return (
      <>
        <section className="bookings">
          <div className="header">
            <Typography variant="h2">
              My upcoming bookings
              {bookingsCount > 0 && bookingsLoaded && (
                <span className="count">({bookingsCount})</span>
              )}
            </Typography>
            {bookingsLoaded && (
              <ExpandToggleButton
                onClick={this.onToggleBookingsExpandedClick}
                expanded={expanded}
              />
            )}
          </div>
          <ToggledContent
            visible={expanded}
            className={!bookingsLoaded ? "loading" : ""}
          >
            {bookingsLoaded && bookingsCount === 0 && (
              <NewResourceButton
                className="new-booking"
                onClick={this.onNewBookingClick}
                label="Create a new booking"
                note="Get in touch to take your space"
              />
            )}
            {!bookingsLoaded && <ProgressSpinner />}
            {futureBookings && bookingsCount > 0 && (
              <>
                <div className="items">
                  {futureBookings.map(this.renderBooking)}
                </div>
              </>
            )}
          </ToggledContent>
        </section>
        {this.renderBookingPopover()}
        <BookingDesignModal
          open={this.state.designModalOpen}
          booking={this.state.designModalBooking}
          onCancelled={this.onBookingDesignModalCancelled}
          onSaved={this.onBookingDesignModalSaved}
        />
      </>
    );
  }

  renderBooking = (booking: ExpandedBooking, key: number) => {
    const hasOutstandingAmount =
      booking.outstandingAmount && booking.outstandingAmount.amount > 0;
    return (
      <div className="booking item" key={key}>
        <h3>{booking.space.name}</h3> {/* TODO: get event/company name */}
        <button
          className="more"
          onClick={(ev) => this.onOpenBookingMenuClick(ev, booking)}
        >
          <MoreIcon />
        </button>
        <div className="location">
          <span className="label">Where</span>
          <span className="value">{booking.space.name}</span>
        </div>
        <div className="date">
          <span className="label">When</span>
          <span className="value">
            {formatBookingDate(booking)}, {formatBookingTime(booking)}
          </span>
        </div>
        {!booking.design && (
          <div className="notice warning">
            <WarningIcon />
            <span>You still need to attach a design to this booking</span>
            <button onClick={() => this.onAddBookingDesignClick(booking)}>
              Click here to attach a design
            </button>
          </div>
        )}
        {hasOutstandingAmount && (
          <div className="notice warning">
            <WarningIcon />
            <span>Payment outstanding</span>
          </div>
        )}
        {booking.design && !hasOutstandingAmount && (
          <div className="notice booking-ok">
            <SuccessIcon />
            <span>This booking is good to go!</span>
          </div>
        )}
      </div>
    );
  };

  renderBookingPopover() {
    const { bookingMenuBooking, bookingMenuAnchorEl } = this.state;
    return (
      <Popover
        className="booking-menu-popover"
        open={!!(bookingMenuAnchorEl && bookingMenuBooking)}
        anchorEl={bookingMenuAnchorEl}
        onClose={this.onCloseBookingMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {bookingMenuBooking && (
          <>
            <Button
              className="add-design"
              onClick={() => this.onAddBookingDesignClick(bookingMenuBooking)}
            >
              <AttachmentIcon /> Attach Design
            </Button>
          </>
        )}
      </Popover>
    );
  }

  loadBookings(promiseContainer: PromiseContainer) {
    promiseContainer
      .add(loadExpandedBookings({ minTime: DateTime.now() }))
      .then(
        (bookings) => this.setState({ futureBookings: bookings }),
        (ex) => {
          handleError("Failed to load bookings", ex);
        }
      );
  }

  onToggleBookingsExpandedClick = () => {
    this.setState({ bookingsExpanded: !this.state.bookingsExpanded });
  };

  onNewBookingClick = () => {
    window.location.href = NextRouteURLs.CONTACT
  };

  onViewBookingChecklistClick = () => {
    window.location.href = NextRouteURLs.CONTACT
  };

  onOpenBookingMenuClick = (ev: MouseEvent, booking: ExpandedBooking) => {
    this.setState({
      bookingMenuAnchorEl: ev.currentTarget as HTMLElement,
      bookingMenuBooking: booking,
    });
  };

  onCloseBookingMenu = () => {
    this.setState({ bookingMenuAnchorEl: null });
  };

  onAddBookingDesignClick = (booking: ExpandedBooking) => {
    this.setState({
      designModalOpen: true,
      designModalBooking: booking,
      bookingMenuAnchorEl: null,
    });
  };

  onBookingDesignModalCancelled = () => {
    this.setState({ designModalOpen: false });
  };

  onBookingDesignModalSaved = () => {
    this.setState({ designModalOpen: false });
    this.loadBookings(this.promiseContainer!);
  };
}

export default withRouter(BookingsSection);
